import React, { memo, useEffect, useMemo, useState, useCallback } from 'react';
import './index.less';
import { Skeleton } from 'antd';
import PrimaryExamPoint from './PrimaryExamPoint';
import ChildExamPoint from './ChildExamPoint';
import InstructionModal from './instructionModal';
import { IHomePageExamSyllabus } from '@/types';
import { useCollapseContext } from '@/pages/prediction-score/context/CollapseContext';
import { useAuthContext } from '@/pages/prediction-score/context/AuthContext';
import { PracticeSettingIcon } from '@/svgs';
import { useAppStore } from '@/store';
import globalStore from '@/store/global';
import { bindEvents } from '@/utils/sensors';
import { InfoCircleOutlined } from '@ant-design/icons';

interface IProps {
    examSyllabus: IHomePageExamSyllabus;
}

const ExamSyllabusCard = (props: IProps) => {
    const {
        themeStore: { theme },
    } = useAppStore();
    const { examSyllabus } = props;
    const { dispatch } = useCollapseContext();
    const loading = useMemo(() => !examSyllabus.examSyllabusId, [examSyllabus.examSyllabusId]);
    const { authUtils } = useAuthContext();

    // 考点任务说明
    const [instructionModalOpen, setInstructionModalOpen] = useState(false);
    const handleOpenInstructionModal = useCallback(() => {
        setInstructionModalOpen(true);
    }, []);
    const handleCloseInstructionModal = useCallback(() => {
        setInstructionModalOpen(false);
    }, []);

    const handleOpenPracticeSetting = authUtils.withLoginCheck(() => {
        const { projectId, subjectId } = examSyllabus;
        globalStore.publish('PracticeSetting', {
            type: 'open',
            params: { projectId, subjectId },
        });

        bindEvents('Click_TIKU_ExerciseSetting', {
            name: '点击练习设置',
            ProjectID: projectId,
        });
    });

    useEffect(() => {
        if (examSyllabus.contents?.length) {
            let defaultCollapseIds: number[] = [];
            // 优先 最近一次的条目
            const lastTimeItem = examSyllabus.contents.find(item => item.lastTimeStatus === 1);
            if (lastTimeItem) {
                defaultCollapseIds = lastTimeItem.contentChildren.filter(item => item.lastTimeStatus === 1).map(item => item.contentId);
                defaultCollapseIds.unshift(lastTimeItem.contentId);
            } else {
                // 没有 最近一次 取第一个条目
                defaultCollapseIds = examSyllabus.contents[0].contentChildren.map(item => item.contentId);
                defaultCollapseIds.unshift(examSyllabus.contents[0].contentId);
            }
            dispatch({
                type: 'BATCH_COLLAPSE',
                ids: defaultCollapseIds,
            });
        }
    }, [examSyllabus.contents]);

    const isEveryChildExamPoint = useMemo(() => examSyllabus.contents?.every(item => item.knowledgeId) || false, [examSyllabus.contents]);

    return (
        <div className="sub-tiku__exam-syllabus-card" id="predict-score-guide">
            <div className="sub-tiku__exam-syllabus-card-header">
                <div className="sub-tiku__exam-syllabus-card-header-title">
                    <span>考点任务</span>
                    <span className="sub-tiku__exam-syllabus-card-header-title-extra" onClick={handleOpenInstructionModal}>
                        <InfoCircleOutlined />
                    </span>
                </div>
                <div className="sub-tiku__exam-syllabus-card-header-extra" onClick={handleOpenPracticeSetting}>
                    <PracticeSettingIcon color={theme.mainColor} />
                    <span>设置</span>
                </div>
            </div>
            {!loading && (
                <div>
                    {examSyllabus.contents.map((item, index) => {
                        const isShowIconLine = isEveryChildExamPoint
                            ? {
                                  prefix: index !== examSyllabus.contents.length - 1,
                                  suffix: index !== 0,
                              }
                            : isEveryChildExamPoint;
                        return item.knowledgeId ? (
                            <ChildExamPoint key={item.contentId} data={item} isShowIconLine={isShowIconLine} />
                        ) : (
                            <PrimaryExamPoint key={item.contentId} data={item} />
                        );
                    })}
                </div>
            )}
            {loading && <Skeleton.Node style={{ width: 700 }} active />}
            <InstructionModal open={instructionModalOpen} onCancel={handleCloseInstructionModal} />
        </div>
    );
};

export default memo(ExamSyllabusCard);
